/**
 * ParticleOverlay — the front-of-house particle entry point.
 *
 * Two render paths:
 *  - `code === 'none'` → render nothing
 *  - `code === 'ambient-orbs'` → render the CSS-only AmbientOrbsOverlay
 *  - any other canvas effect code → render <ParticleCanvas> wrapped in
 *    an intensity-controlled div
 *
 * Z-index is fixed at 3 by the wrapper so it sits on top of the
 * radial gradient background and below every slide content + chrome.
 */
import { useEffect, useState } from 'react';
import type { CanvasEffectId } from './types';
import { ParticleCanvas } from './ParticleCanvas';
import { AmbientOrbsOverlay } from './AmbientOrbsOverlay';
import { CssEffectOverlay, CSS_EFFECT_CODES } from '../../../viewer/effects/CssEffectOverlay';
import { IndexAmbientOverlay, INDEX_AMBIENT_EFFECT_CODES } from '../../../viewer/effects/IndexAmbientOverlay';

const CANVAS_EFFECT_CODES: ReadonlySet<string> = new Set<CanvasEffectId>([
  'floating-heart-bokeh',
  'rose-petal-cascade',
  'sparkler-trail-hearts',
  'constellation-connection',
  'velvet-dust-motes',
  'heartbeat-pulse-rings',
  'champagne-bubble-rise',
  'firefly-courtship',
  'silk-ribbon-flow',
  'diamond-dust-sparkle',
  'pheromone-trail',
  'aurora-heart',
  'floating-kiss-marks',
  'memory-fragment-drift',
  'entwined-particle-vines',
]);

interface ParticleOverlayProps {
  code: string;
  /** 0..1 — clamped before being applied as the wrapper's `opacity`. */
  intensity?: number;
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  return reduced;
}

export function ParticleOverlay({ code, intensity = 0.6 }: ParticleOverlayProps) {
  const reducedMotion = usePrefersReducedMotion();
  if (!code || code === 'none') return null;
  if (code === 'ambient-orbs') {
    return (
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden"
        style={{ zIndex: 3, opacity: Math.max(0, Math.min(1, intensity)) }}
        aria-hidden="true"
      >
        <AmbientOrbsOverlay />
      </div>
    );
  }
  if (INDEX_AMBIENT_EFFECT_CODES.includes(code as (typeof INDEX_AMBIENT_EFFECT_CODES)[number])) {
    return (
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden"
        style={{ zIndex: 3, opacity: Math.max(0, Math.min(1, intensity)) }}
        aria-hidden="true"
      >
        <IndexAmbientOverlay code={code} intensity={1} reducedMotion={reducedMotion} />
      </div>
    );
  }
  if (CSS_EFFECT_CODES.has(code)) {
    return (
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden"
        style={{ zIndex: 3, opacity: Math.max(0, Math.min(1, intensity)) }}
        aria-hidden="true"
      >
        <CssEffectOverlay code={code} intensity={intensity} />
      </div>
    );
  }
  if (!CANVAS_EFFECT_CODES.has(code)) return null;
  const effect = code as CanvasEffectId;
  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden"
      style={{ zIndex: 3, opacity: Math.max(0, Math.min(1, intensity)) }}
      aria-hidden="true"
    >
      <ParticleCanvas effect={effect} reducedMotion={reducedMotion} />
    </div>
  );
}
